import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import MdaePopup, {
  MdaePopupContent,
  MdaePopupFooter,
  MdaePopupFooterEndButtons,
  MdaePopupFooterStartButtons,
  MdaePopupTitle,
} from "../../../common_components/common_widgets/popup";
import MdaeButton from "../../../common_components/common_widgets/button";
import { TOGGLE_REASSIGN_PROCESS_MANAGER_POPUP } from "./reducers/case-list-reducer";
import { HIDE_LOADER, SHOW_LOADER } from "../../../common_components/mdae-common-reducer";
import { BULK_UPDATE_EA_CASE_PROCESS_MANAGER } from "./services/bulk-update-case-process-manager";
import { GET_PROCESS_MANAGERS_LIST } from "../services/get-process-managers-list";
import { actions, callOperation } from "async-ops";
import MdaeSelectBox from "../../../common_components/common_widgets/select-box/component";
import { SET_CASE_FORM_DATA } from "../case-detail/case-data/reducers/case-data-reducer";
import mdaeNotify, { NOTIFY_MESSAGE_TYPE } from "../../../common_components/common_widgets/toast/notify";

export const toggleReassignProcessManagerPopup = (isVisible,requiredAuditGroupIds, dispatch) => {
  dispatch({
    type: TOGGLE_REASSIGN_PROCESS_MANAGER_POPUP,
    data: {
      isReassignProcessManagerPopupVisible: isVisible,
      requiredAuditGroupIds
    },
  });
};
interface IReassignProcessManagerPopup {
  selectedRowKeys:any[]
}

const ReassignProcessManagerPopup: React.FC<IReassignProcessManagerPopup> = (props) => {
  const dispatch = useDispatch();

  const selectedProcessManagerId = useSelector(
    (state) => state["caseDataReducer"].caseData.reassignedCaseProcessManagerId,
    isEqual
  );
  const isReassignProcessManagerPopupVisible = useSelector(
    (state) => state["caseListReducer"].isReassignProcessManagerPopupVisible,
    isEqual
  );
  const requiredAuditGroupIds = useSelector(
    (state) => state["caseListReducer"].requiredAuditGroupIds,
    isEqual
  );
  const caseData = useSelector(
    (state) => state["caseDataReducer"].caseData,
    isEqual
  );
  const allProcessManagersList = useSelector(
    (state) => state["auditPropertiesReducer"].allProcessManagersList,
    isEqual
  );
  useEffect(() => {
    if (allProcessManagersList?.length == 0 || !allProcessManagersList) {
      dispatch(
        actions.asyncOperationStart(
          GET_PROCESS_MANAGERS_LIST,
          "External Audit Creation"
        )
      );
    }
  }, [dispatch]);

  const managerDataSource = useMemo(() => {
    if (!allProcessManagersList?.length || !requiredAuditGroupIds?.length) {
      return [];
    }
  
    const groupedManagers = allProcessManagersList.reduce((acc: Record<number, any>, curr: any) => {
      if (!acc[curr.auditUserId]) {
        acc[curr.auditUserId] = { ...curr, allowedIds: new Set() };
      }
      acc[curr.auditUserId].allowedIds.add(curr.auditGroupId);
      return acc;
    }, {});
  
    return Object.values(groupedManagers)
      .filter((manager: any) => {
        return requiredAuditGroupIds.every(id => manager.allowedIds.has(id));
      })
      .sort((a: any, b: any) => a.fullName.localeCompare(b.fullName)); 
  
  }, [allProcessManagersList, requiredAuditGroupIds]);
  
  
  const handleCancelClick = useCallback(() => {
    toggleReassignProcessManagerPopup(false,[], dispatch);
  }, [dispatch]);

  const handleUpdateClick = useCallback(() => {
    if (props.selectedRowKeys && props.selectedRowKeys.length > 0 && selectedProcessManagerId>0) {
      const selectedRowCount = props.selectedRowKeys.length;
      
      const payload = {
        eaCaseIds: props.selectedRowKeys,
        caseProcessManagerId: selectedProcessManagerId,
      };
      dispatch({ type: SHOW_LOADER });
      callOperation(BULK_UPDATE_EA_CASE_PROCESS_MANAGER, payload)
        .then(() => {
          mdaeNotify({
            message: `${selectedRowCount} ${selectedRowCount > 1 ? 'Cases' : 'Case'} Updated Successfully.`,
            messageType: NOTIFY_MESSAGE_TYPE.SUCCESS,
          });
          dispatch({
            type: SET_CASE_FORM_DATA,
            data: {
              caseData: {
                reassignedCaseProcessManagerId: null,
              },
            },
          });
          toggleReassignProcessManagerPopup(false, [], dispatch);
          dispatch({ type: HIDE_LOADER });
        })
    }
  }, [dispatch, props.selectedRowKeys, selectedProcessManagerId]);

  const popupTitle = useMemo(() => {
    return (
      <MdaePopupTitle isSmallPopup closePopupHandler={handleCancelClick}>
        Reassign Case Process Manager
      </MdaePopupTitle>
    );
  }, [handleCancelClick]);

  const popupContent = useCallback(() => {
    return (
      <MdaePopupContent>
        <MdaeSelectBox
          id={"Manager"}
          tabIndex={1}
          mdaeDropDownBoxTitle={"Case Process Manager"}
          displayExpr={"fullName"}
          valueExpr={"auditUserId"}
          value={caseData.reassignedCaseProcessManagerId}
          dataSource={managerDataSource}
          onValueChanged={(e) => {
            dispatch({
              type: SET_CASE_FORM_DATA,
              data: {
                caseData: {
                  reassignedCaseProcessManagerId: e.value,
                },
              },
            });
          }}
        >
        </MdaeSelectBox>
      </MdaePopupContent>
    );
  }, [managerDataSource, caseData.reassignedCaseProcessManagerId, dispatch]);

  const popupFooter = useCallback(() => {
    return (
      <MdaePopupFooter>
        <MdaePopupFooterStartButtons>
          <MdaeButton
            id="cancel-update-of-case-process-manager"
            role={"secondary"}
            onClick={handleCancelClick}
          >
            Cancel
          </MdaeButton>
        </MdaePopupFooterStartButtons>

        <MdaePopupFooterEndButtons>
          <MdaeButton
            id="update-case-process-manager"
            role={"primary"}
            onClick={handleUpdateClick}
          >
            Update
          </MdaeButton>
        </MdaePopupFooterEndButtons>
      </MdaePopupFooter>
    );
  }, [handleCancelClick, handleUpdateClick]);

  return (
    <div>
      <MdaePopup
        deferRendering={false}
        width={450}
        height={260}
        className="reassign-process-manager-popup"
        mdaeTitleRender={() => popupTitle}
        visible={isReassignProcessManagerPopupVisible}
        mdaeDataUpdaterConfig={[
          {
            actionType: TOGGLE_REASSIGN_PROCESS_MANAGER_POPUP,
            reducerName: "caseListReducer",
            widgetOptionBindings: {
              visible: "isReassignProcessManagerPopupVisible",
            },
          },
        ]}
        mdaeContentRender={popupContent}
        mdaeFooterRender={popupFooter}
      />
    </div>
  );
};

export default ReassignProcessManagerPopup;
